import { FormikProps } from 'formik';
import React, { useRef, useMemo } from 'react';

import Error from 'components/Error';
import Page from 'components/Page';
import VisuallyHidden from 'components/VisuallyHidden';
import hooks from 'hooks';
import * as errors from 'utils/errors';

import Navigation from './Navigation';
import Stepper from './Stepper';
import { useWizardContext } from './context';
import * as wizardHooks from './hooks';
import { WizardProps } from './interfaces';
import * as Styled from './styled';

function Wizard<T>({
  isHashEnabled = true,
  isStepperEnabed = false,
  opacityBetweenLoading,
  showHeader = true,
  showFooter = false,
  onWindowClose = () => {},
  breadCrumbs = [],
  storage,
  stepsComponents,
  headerProps,
  children,
  scrollOptions,
  scrollOnSubmitError
}: WizardProps<T>) {
  /**
   * Keep data of latest form information without re-rendering it, needed for the "onMount" event handler
   *
   * Notes:
   *
   * https://stackoverflow.com/questions/56455887/react-usestate-or-useref
   * https://github.com/facebook/react/issues/14387#issuecomment-503616820
   */
  const formData = useRef<FormikProps<T>>();
  const startStep = useRef<HTMLAnchorElement>(null);

  /**
   * Access the wizard context for the <Page /> update changes
   */
  const wizardContext = useWizardContext();
  const { currentStepId, steps } = wizardContext;

  /**
   * Get the stepper items collection (list of unique titles to display in the stepper (some steps need to display on same stepper item, hence need to remove duplicates)
   */
  const stepperItems = useMemo(() => {
    const data = Object.values(steps ?? {}) as { stepName?: string }[];
    return Array.from(new Set(data.filter(s => !!s.stepName).map(s => s.stepName)));
  }, [steps]) as string[];

  const wizardContextStep = steps[currentStepId];
  if (!wizardContextStep) {
    throw new errors.SVError(
      'Invalid stepId was provided to Wizard while navigating to a given step'
    );
  }

  const focusStart = React.useCallback(() => {
    startStep.current?.focus();
  }, []);

  // User triggered a new step change event
  // ---------------------------------------
  wizardHooks.useTransition(scrollOptions, focusStart);

  // Extract the given step details object from the collection of "steps" calculated before
  const { id, title, navigation, validation, stepName = '' } = wizardContextStep;
  // Extract the "Component" part of the user "steps" input (managed outside of "wizardContextStep" due to issues with persisting the lazy component to the <ContextProvider />)
  const Component = stepsComponents[id];

  /**
   * Apply custom hook for adding hash key to the route on step change
   */
  isHashEnabled &&
    hooks.useHash(currentStepId, stepId => {
      wizardContext.dispatch({
        type: 'GOTO_STEP',
        stepId
      });
    });

  /**
   *
   */
  hooks.useBeforeUnload(() => {
    formData.current && onWindowClose(formData.current, wizardContext.appStates);
  });

  /**
   * render functionality from wrapper component
   * @param formProps formik properties
   */
  const renderStep = (formProps: FormikProps<T>) => {
    // Update the formData "ref" variable with latest formProps (used later in the "useBeforeUnload" hook to notify related app when the browser window is closed)
    formData.current = formProps;

    wizardHooks.usePersist(formProps, storage);

    // Render the step "Component" element with its children as the "Buttons" Naviation container
    return (
      <React.Fragment>
        <Page
          headerProps={headerProps}
          title={title}
          loading={wizardContext.isPending}
          opacityBetweenLoading={opacityBetweenLoading}
          breadCrumbs={breadCrumbs}
          error={wizardContext.error ? <Error>{wizardContext.error.message}</Error> : undefined}
          showHeader={showHeader}
          showFooter={showFooter}
          StaticContent={
            isStepperEnabed ? <Stepper items={stepperItems} activeStepName={stepName} /> : undefined
          }>
          <Styled.ContentWrapper>
            <Styled.ComponentWrapper>
              <VisuallyHidden>
                {/* Anchors without a href are unfocusable - need to set a tabIndex to be able to focus it */}
                <a ref={startStep} id='start-step' tabIndex={0}>
                  You are on the &quot;{stepName}&quot; step
                </a>
              </VisuallyHidden>
              <Component
                id={id}
                title={title}
                stepName={stepName}
                navigation={navigation}
                validation={validation}
                context={{
                  form: formProps,
                  wizard: {
                    dispatch: wizardContext.dispatch
                  },
                  appStates: wizardContext.appStates
                }}
              />
              <Navigation
                navigation={navigation}
                validation={validation}
                scrollOnSubmitError={scrollOnSubmitError}
              />
            </Styled.ComponentWrapper>
          </Styled.ContentWrapper>
        </Page>
      </React.Fragment>
    );
  };

  /**
   * Render the Wizard using Render Props pattern where getting a reference to the FormikProps from the Wrapper component
   */
  return children({
    renderStep
  });
}

export default Wizard;
